import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { WarehouseLocation } from '../models/warehouse-location.model';

@Injectable({
  providedIn: 'root'
})
export class WarehouseLocationService {

  /**
  * Endpoint for http calls
  */
  private _endpoint: string = environment.api.endpoint;

  constructor(private httpClient: HttpClient) { }

  /**
   * Get a warehouse location from the backend by id
   *
   * @param id number id of warehouse location to get
   *
   * @returns Observable<WarehouseLocation>
   */
  getWarehouseLocationById(id: number): Observable<WarehouseLocation> {

    if (id <= 0 || id === null || id === undefined) {
      throw new Error('WarehouseLocationService: Id must be a valid number');
    }

    return this.httpClient.get(this._endpoint + 'warehouse-location/' + id + '?join=warehouse').pipe(map((value: any) => {
      return <WarehouseLocation>value;
    }));
  }

  getAllWarehouseLocations(): Observable<WarehouseLocation[]> {
    return this.httpClient.get(this._endpoint + 'warehouse-location?join=warehouse&sort=aisle,ASC&sort=shelf,ASC')
      .pipe(map((data) => {
        return <WarehouseLocation[]>data;
      }));
  }

  /**
   * Locations scoped to a single warehouse, for the location pickers on the stock screens.
   *
   * @param warehouseId number id of the warehouse to get locations for
   *
   * @returns Observable<WarehouseLocation[]>
   */
  getWarehouseLocationsForWarehouse(warehouseId: number): Observable<WarehouseLocation[]> {

    if (!warehouseId || warehouseId === -1) {
      throw new Error('You must supply a valid id in getWarehouseLocationsForWarehouse');
    }

    return this.httpClient.get(this._endpoint + 'warehouse-location?filter=warehouseId||eq||' + warehouseId
      + '&join=warehouse&sort=aisle,ASC&sort=shelf,ASC').pipe(map((data) => {
        return <WarehouseLocation[]>data;
      }));
  }

  /**
   * creates a warehouse location in the backend based on warehouse location object.
   *
   * @param warehouseLocation warehouse location object.
   *
   * @returns Observable<WarehouseLocation>
   */
  createWarehouseLocation(warehouseLocation: WarehouseLocation): Observable<WarehouseLocation> {

    if (!warehouseLocation) {
      throw new Error('warehouse location service: you must supply a warehouse location object for saving');
    }

    delete warehouseLocation.createdAt;
    delete warehouseLocation.updatedAt;
    delete warehouseLocation.warehouse;

    if (warehouseLocation.id > 0) {
      throw new Error('warehouse location service: Cannot create a existing object');
    }

    delete warehouseLocation.id;

    return this.httpClient.post(this._endpoint + 'warehouse-location',
      JSON.stringify(warehouseLocation),
      { headers: { 'Content-Type': 'application/json' } }).pipe(map((data) => {
        return <WarehouseLocation>data;
      }));
  }

  /**
   * updates a warehouse location in the backend based on warehouse location object.
   *
   * @param warehouseLocation warehouse location object.
   *
   * @returns Observable<WarehouseLocation>
   */
  updateWarehouseLocation(warehouseLocation: WarehouseLocation): Observable<WarehouseLocation> {

    if (!warehouseLocation) {
      throw new Error('warehouse location service: you must supply a warehouse location object for saving');
    }

    delete warehouseLocation.createdAt;
    delete warehouseLocation.updatedAt;
    delete warehouseLocation.warehouse;

    if (!warehouseLocation.id || warehouseLocation.id === -1) {
      throw new Error('warehouse location service: Cannot update an record without id');
    }

    return this.httpClient.put(this._endpoint + 'warehouse-location/' + warehouseLocation.id,
      JSON.stringify(warehouseLocation),
      { headers: { 'Content-Type': 'application/json' } }).pipe(map((data) => {
        return <WarehouseLocation>data;
      }));
  }
}
